import { zodResolver } from "@hookform/resolvers/zod";
import { usePrefetchQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  buildODataFilter,
  ControlCategory,
  cn,
  getSCMCategoryFromControlCategory,
  queries,
  useAuthConfig,
  View,
} from "@theprelude/core";
import {
  Button,
  CheckmarkIcon,
  CloseIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownFormField,
  EditorIcon,
  Form,
  FunnelIcon,
  IconButton,
  Input,
  InputField,
  notifyError,
  notifySuccess,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TrashcanIcon,
} from "@theprelude/ds";
import { Suspense, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { usePopper } from "@/hooks/use-popper";
import {
  useCreateView,
  useDeleteView,
  useUpdateView,
} from "@/hooks/views-mutations";
import { Filter } from "@/lib/filter";
import { parseFilter } from "@/lib/odata";
import { AvailableFilter } from "../filter-form/available-filters";
import {
  FilterForm,
  FilterFormHandle,
  getFilterCount,
} from "../filter-form/filter-form";
import { getOdataFilters } from "../filter-form/odata";
import { isTopLevelView } from "../filter-form/top-level-form";

export function TableFilterSelector({
  availableFilters,
  currentFilters,
  setFilters,
  category,
}: {
  availableFilters: AvailableFilter[];
  currentFilters: Filter[];
  setFilters: (filters: Filter[]) => void;
  category: ControlCategory;
}) {
  const authConfig = useAuthConfig();
  usePrefetchQuery(queries.scm.views(authConfig));

  const popper = usePopper();
  const filterCount = getFilterCount(currentFilters);

  return (
    <Popover {...popper}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "flex h-9 items-center justify-center border border-stroke-line",
            popper.open && "border-primary-foreground bg-secondary-background",
            currentFilters.length > 0 &&
              "border-primary-foreground bg-secondary-background",
          )}
        >
          <FunnelIcon className="size-4 text-secondary-foreground" />
          {currentFilters.length > 0 && (
            <div
              className={cn(
                "inline-flex items-center justify-center rounded-2xl bg-primary-background px-2 py-0 font-normal text-[0.625rem] text-primary-foreground dark:text-primary-foreground",
              )}
            >
              {filterCount}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <TableFilterForm
          onClose={popper.close}
          availableFilters={availableFilters}
          currentFilters={currentFilters}
          setFilters={setFilters}
          category={category}
        />
      </PopoverContent>
    </Popover>
  );
}

function useSaveView({ category }: { category: ControlCategory }) {
  const [currentView, setCurrentView] = useState<string | null>(null);

  const createView = useCreateView();
  const updateView = useUpdateView();

  async function updateOrSaveView({
    name,
    filter,
  }: {
    name?: string;
    filter: string;
  }) {
    try {
      if (currentView) {
        await updateView.mutateAsync({
          category,
          filter,
          viewId: currentView,
        });
        notifySuccess({ message: "View updated successfully!" });
      } else {
        if (name) {
          const view = await createView.mutateAsync({
            category,
            filter,
            name,
          });

          setCurrentView(view.view_id);
          notifySuccess({ message: "View created successfully!" });
        } else {
          throw new Error("Name is required");
        }
      }
    } catch (error) {
      notifyError({ error, message: "Failed to save view." });
    }
  }

  return {
    updateOrSaveView,
    currentView,
    setCurrentView,
    updateView,
    isPending: createView.isPending || updateView.isPending,
  };
}

function TableFilterForm({
  onClose,
  availableFilters,
  currentFilters,
  setFilters,
  category,
}: {
  onClose: () => void;
  availableFilters: AvailableFilter[];
  currentFilters: Filter[];
  setFilters: (filters: Filter[]) => void;
  category: ControlCategory;
}) {
  const filterFormRef = useRef<FilterFormHandle>(null);

  const {
    updateOrSaveView,
    setCurrentView,
    updateView,
    currentView,
    isPending,
  } = useSaveView({
    category,
  });

  const [showNameDialog, setShowNameDialog] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<Filter[] | null>(null);

  const scmCategory = getSCMCategoryFromControlCategory(category);

  const nameForm = useForm<{ name: string }>({
    defaultValues: { name: "" },
  });

  const handleViewSelection = (view: View | null) => {
    if (view) {
      setCurrentView(view.id);
      const newFilters = parseFilter(view.filter);
      filterFormRef.current?.resetForm(newFilters);
    } else {
      setCurrentView(null);
      filterFormRef.current?.resetForm([]);
    }
  };

  async function onSubmit(data: Filter[]) {
    setFilters(data);
    onClose();
  }

  async function onSave(data: Filter[]) {
    if (!currentView) {
      setPendingFilters(data);
      setShowNameDialog(true);
      return;
    }

    await updateOrSaveView({
      filter: buildODataFilter({
        filter: getOdataFilters(data, false),
      }),
    });
  }

  async function handleNameSubmit(data: { name: string }) {
    if (!pendingFilters) return;
    try {
      await updateOrSaveView({
        name: data.name,
        filter: buildODataFilter({
          filter: getOdataFilters(pendingFilters, false),
        }),
      });

      setShowNameDialog(false);
      setPendingFilters(null);
      nameForm.reset();
    } catch {
      notifyError({ message: "Failed to save view" });
    }
  }

  function handleDialogClose() {
    setShowNameDialog(false);
    setPendingFilters(null);
    nameForm.reset();
  }

  return (
    <div className="flex flex-col">
      <Suspense fallback={null}>
        <SavedViews
          selectedViewId={currentView}
          onSelect={handleViewSelection}
          updateView={updateView}
          category={category}
        />
      </Suspense>
      <FilterForm
        ref={filterFormRef}
        scmCategory={scmCategory}
        availableFilters={availableFilters}
        defaultFilters={currentFilters}
        onSubmit={onSubmit}
        onClear={() => {
          setFilters([]);
          onClose();
        }}
        onClose={onClose}
        onSave={onSave}
      />
      <Dialog
        open={showNameDialog}
        onOpenChange={(open) => {
          if (!open) handleDialogClose();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name your view</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Form {...nameForm} onSubmitValid={handleNameSubmit}>
              <div className="flex flex-col gap-2">
                <InputField error={nameForm.formState.errors.name?.message}>
                  <Input
                    {...nameForm.register("name", { required: true })}
                    autoFocus
                    placeholder="Enter view name"
                    disabled={isPending}
                  />
                </InputField>
                <DialogFooter>
                  <Button
                    intent="primary"
                    type="submit"
                    loading={isPending}
                    disabled={isPending}
                  >
                    Save
                  </Button>
                  <Button
                    intent="secondary"
                    type="button"
                    onClick={handleDialogClose}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                </DialogFooter>
              </div>
            </Form>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ViewSelectorDisplayProps {
  selectedViewId: string | null;
  handleSelectView: (viewId: string | null) => void;
  handleEditName: () => void;
  handleDeleteView: () => void;
  deleteIsPending: boolean;
  category: ControlCategory;
}

const ViewSelectorDisplay = ({
  selectedViewId,
  handleSelectView,
  handleEditName,
  handleDeleteView,
  deleteIsPending,
  category,
}: ViewSelectorDisplayProps) => {
  const authConfig = useAuthConfig();
  const savedViewsQuery = useSuspenseQuery({
    ...queries.scm.views(authConfig),
    select: (data) =>
      data.filter(
        (view) => view.category === category && !isTopLevelView(view.name),
      ),
  });

  const form = useForm<{ savedView: string }>({
    defaultValues: { savedView: selectedViewId || "" },
  });

  useEffect(() => {
    if (selectedViewId) {
      form.setValue("savedView", selectedViewId);
    }
  }, [selectedViewId, form]);

  return (
    <FormProvider {...form}>
      <form className="flex items-center gap-2 px-4 pt-4">
        <DropdownFormField
          className="w-[200px]"
          contentClassName="w-[300px]"
          placeholder="Select a saved view"
          options={
            savedViewsQuery.data?.map((view: View) => ({
              label: view.name,
              value: view.id,
            })) ?? []
          }
          {...form.register("savedView", {
            onChange: (e) => {
              handleSelectView(e.target.value as string | null);
            },
          })}
        />
        {selectedViewId && (
          <>
            <IconButton
              icon={<EditorIcon className="size-4" />}
              onClick={handleEditName}
              disabled={deleteIsPending}
            />
            <IconButton
              icon={<TrashcanIcon className="size-4" />}
              onClick={handleDeleteView}
              disabled={deleteIsPending}
              loading={deleteIsPending}
            />
          </>
        )}
      </form>
    </FormProvider>
  );
};

interface ViewNameEditorProps {
  initialName: string;
  selectedViewId: string;
  handleSaveNameSubmit: (data: { newName: string }, viewId: string) => void;
  handleCancelEdit: () => void;
  saveIsPending: boolean;
}

const ViewNameEditor = ({
  initialName,
  selectedViewId,
  handleSaveNameSubmit,
  handleCancelEdit,
  saveIsPending,
}: ViewNameEditorProps) => {
  const editorForm = useForm<{ newName: string }>({
    defaultValues: { newName: initialName || "" },
    resolver: zodResolver(z.object({ newName: z.string().min(1) })),
  });

  const onSubmit = (data: { newName: string }) => {
    handleSaveNameSubmit(data, selectedViewId);
  };

  return (
    <FormProvider {...editorForm}>
      <form
        onSubmit={editorForm.handleSubmit(onSubmit)}
        className="flex items-center gap-2 px-4 pt-4"
      >
        <InputField
          className="w-[200px]"
          error={editorForm.formState.errors.newName?.message}
        >
          <Input
            {...editorForm.register("newName", {
              required: "Name cannot be empty",
            })}
            autoFocus
            className="h-[38px]"
            disabled={saveIsPending}
          />
        </InputField>
        <IconButton
          icon={<CheckmarkIcon />}
          type="submit"
          disabled={saveIsPending}
          loading={saveIsPending}
        />
        <IconButton
          icon={<CloseIcon />}
          onClick={handleCancelEdit}
          disabled={saveIsPending}
        />
      </form>
    </FormProvider>
  );
};

function SavedViews({
  onSelect,
  updateView,
  selectedViewId,
  category,
}: {
  onSelect: (view: View | null) => void;
  updateView: ReturnType<typeof useUpdateView>;
  selectedViewId: string | null;
  category: ControlCategory;
}) {
  const authConfig = useAuthConfig();

  const savedViewsQuery = useSuspenseQuery({
    ...queries.scm.views(authConfig),
    select: (data) =>
      data.filter(
        (view) => view.category === category && !isTopLevelView(view.name),
      ),
  });

  const deleteViewMutation = useDeleteView();

  const [isEditingName, setIsEditingName] = useState(false);

  if (savedViewsQuery.data?.length === 0) {
    return null;
  }

  const handleSelectView = (viewId: string | null) => {
    if (!viewId) {
      onSelect(null);
      setIsEditingName(false);
      return;
    }
    const view = savedViewsQuery.data?.find((v) => v.id === viewId);
    if (view) {
      onSelect(view);
    } else {
      onSelect(null);
    }
    setIsEditingName(false);
  };

  const handleEditNameUI = () => {
    setIsEditingName(true);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
  };

  const handleSaveNameFromEditor = (
    formData: { newName: string },
    viewId: string,
  ) => {
    const newNameTrimmed = formData.newName.trim();
    if (!newNameTrimmed) {
      notifyError({ message: "View name cannot be empty." });
      return;
    }

    updateView.mutate(
      { viewId, name: newNameTrimmed },
      {
        onSuccess: () => {
          notifySuccess({ message: "View name updated successfully!" });
          setIsEditingName(false);
        },
        onError: (error) => {
          notifyError({ error, message: "Failed to update view name." });
        },
      },
    );
  };

  const handleDeleteView = () => {
    if (!selectedViewId) return;

    deleteViewMutation.mutate(selectedViewId, {
      onSuccess: () => {
        notifySuccess({ message: "View deleted successfully!" });
        onSelect(null);
      },
      onError: (error) => {
        notifyError({ error, message: "Failed to delete view." });
      },
    });
  };

  if (isEditingName && selectedViewId) {
    return (
      <ViewNameEditor
        initialName={
          savedViewsQuery.data?.find((v) => v.id === selectedViewId)?.name || ""
        }
        selectedViewId={selectedViewId}
        handleSaveNameSubmit={handleSaveNameFromEditor}
        handleCancelEdit={handleCancelEdit}
        saveIsPending={updateView.isPending}
      />
    );
  }

  return (
    <ViewSelectorDisplay
      selectedViewId={selectedViewId}
      handleSelectView={handleSelectView}
      handleEditName={handleEditNameUI}
      handleDeleteView={handleDeleteView}
      deleteIsPending={deleteViewMutation.isPending}
      category={category}
    />
  );
}
